import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation, Link } from "react-router-dom";
import LoadingSpinner from "./LoadingSpinner";
import * as localService from "../services/localService";
import { useSubscription } from "../hooks/useSubscription";
import { isOrgAdmin, getOrgContactEmail, getOrgName } from "../utils/orgAdminAccess";
import { Mail, Copy, Check, ShieldAlert } from "lucide-react";

const SubscriptionWall = ({ children }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [copiedEmail, setCopiedEmail] = useState(false);

  // Get user from localStorage (more reliable than Redux for org type info)
  const user = localService.getUser();
  const { loading, activeSubscription, error, hasActiveSubscription } = useSubscription();
  const userIsOrgAdmin = isOrgAdmin(user);
  const orgContactEmail = getOrgContactEmail(user);
  const organizationName = getOrgName(user);

  const handleCopyEmail = () => {
    if (!orgContactEmail) return;
    navigator.clipboard.writeText(orgContactEmail);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  // Routes that are exempt from subscription wall
  const exemptRoutes = [
    "/host/pricing",
    "/subscriber/pricing", // Subscriber pricing page
    "/host/subscription-upgrade",
    "/signin-web",
    "/otp-confirmation",
    "/host/settings",
    "/host/payment-setup",
  ];

  // Check if current route is exempt
  const isExemptRoute = exemptRoutes.some(route => location.pathname.startsWith(route));

  // Immediate check - before any useEffect runs
  const { organization, organizationRole } = user || {};
  const orgTypeCode = organization?.type?.code;
  const roleCode = organizationRole?.code;
  // Check for debug skip flag (only works on localhost)
  const isDebugSkip =
    window.location.hostname === "localhost" && localStorage.getItem("debug_skip_subscription") === "true";

  // Organization types that require subscription (excluding ROLE_HOST_CUSTOMER_INDIVIDUAL)
  // HOST_PROPERTY_MANAGEMENT and CORPORATE_CUSTOMER require subscription
  // ROLE_HOST_CUSTOMER_INDIVIDUAL does not require subscription even under these org types
  const requiresSubscription =
    (orgTypeCode === "HOST_PROPERTY_MANAGEMENT" || orgTypeCode === "CORPORATE_CUSTOMER") &&
    roleCode !== "ROLE_HOST_CUSTOMER_INDIVIDUAL";

  useEffect(() => {
    // Skip if no user or no organization data
    if (!user || !user.organization) {
      return;
    }

    // Skip if on exempt route or debug skip is active
    if (isExemptRoute || isDebugSkip) {
      return;
    }

    if (requiresSubscription && !hasActiveSubscription) {
      // CORPORATE_CUSTOMER should go to subscriber pricing, not host pricing
      const redirectPath = orgTypeCode === "CORPORATE_CUSTOMER" ? "/subscriber/pricing" : "/host/pricing";
      history.replace(redirectPath);
    }
  }, [
    user,
    hasActiveSubscription,
    history,
    location.pathname,
    isExemptRoute,
    isDebugSkip,
    orgTypeCode,
    roleCode,
    requiresSubscription,
  ]);

  // Clear stale error persisted from a previous session on first load.
  // Without this, the error screen blocks children from rendering, so
  // AuthenticatedLayout's subscription check effect never fires and the error
  // can never be cleared (chicken-and-egg problem with Redux Persist).
  const hasClearedStaleError = useRef(false);
  useEffect(() => {
    if (error && !loading && !hasClearedStaleError.current && requiresSubscription) {
      hasClearedStaleError.current = true;
      dispatch({ type: "SET_SUBSCRIPTIONS_ERROR", payload: null });
    }
  }, [error, loading, requiresSubscription]);

  // Show loading spinner while checking
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner
          loading={true}
          message="Checking subscription status..."
          padding="py-8"
          className="text-blue-600"
        />
      </div>
    );
  }

  // Show error state if there was an error and we're not on exempt route
  if (error && !isExemptRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-red-600 text-lg font-semibold mb-4">Error checking subscription</div>
          <div className="text-gray-600 mb-6">{error}</div>
          <button
            onClick={() => {
              dispatch({ type: "SET_SUBSCRIPTIONS_ERROR", payload: null });
            }}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
          <Link to="/host/settings" className="text-sm text-gray-500 hover:text-gray-700 underline underline-offset-2">
            Go to Settings
          </Link>
        </div>
      </div>
    );
  }

  // IMMEDIATE BLOCK - This runs on every render BEFORE useEffect
  // If user requires subscription but doesn't have one and not on exempt route, block immediately
  if (requiresSubscription && !hasActiveSubscription && !isExemptRoute && !isDebugSkip) {
    const pricingRedirectPath =
      orgTypeCode === "CORPORATE_CUSTOMER" ? "/subscriber/pricing" : "/host/pricing";

    if (!userIsOrgAdmin) {
      const emailSubject = encodeURIComponent("Subscription Upgrade Request");
      const emailBody = encodeURIComponent(
        `Hi Admin,\n\nOur team needs a subscription plan upgrade on SPACIA for ${organizationName} to access platform features.\n\nThank you!`
      );
      const mailtoUrl = orgContactEmail
        ? `mailto:${orgContactEmail}?subject=${emailSubject}&body=${emailBody}`
        : null;

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
          <div className="text-center max-w-lg mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 p-8 sm:p-10">
            <div className="w-16 h-16 bg-blue-50 text-blue-600 border border-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <ShieldAlert className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Subscription Upgrade Required</h1>
            <p className="text-gray-600 text-sm mb-6 leading-relaxed">
              Your organization (<span className="font-semibold text-gray-800">{organizationName}</span>) requires an active subscription to access this platform. Only organization administrators can purchase or upgrade plans.
            </p>

            {orgContactEmail ? (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6 text-left">
                <span className="text-xs uppercase font-semibold text-gray-500 tracking-wider block mb-1">
                  Organization Admin Contact
                </span>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {orgContactEmail}
                  </span>
                  <button
                    type="button"
                    onClick={handleCopyEmail}
                    className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-200 rounded-md transition-colors flex-shrink-0"
                    title="Copy email address"
                  >
                    {copiedEmail ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            ) : null}

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              {orgContactEmail && (
                <a
                  href={`mailto:${orgContactEmail}?subject=${emailSubject}&body=${emailBody}`}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email Admin
                </a>
              )}
              <button
                type="button"
                onClick={() => history.push(pricingRedirectPath)}
                className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-2.5 bg-gray-100 text-gray-700 font-medium text-sm rounded-lg hover:bg-gray-200 transition-colors"
              >
                View Available Plans
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Subscription Required</h1>
          <p className="text-gray-600 mb-6">
            You need an active subscription to access the platform. Please subscribe to a package to continue.
          </p>
          <button
            onClick={() => {
              history.push(pricingRedirectPath);
            }}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            View Pricing Plans
          </button>
        </div>
      </div>
    );
  }

  // Render children if all checks pass
  return children;
};

export default SubscriptionWall;
